import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, UserPlus, Trash2, Edit3, Loader2, ArrowLeft, Check, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE } from "../lib/apiBase";

const API = `${API_BASE}/api`;

const PLANS = ["free", "unlimited"];
const LABELS = [
  { value: "normal", display: "Normal" },
  { value: "guest", display: "Invitado" },
  { value: "beta_tester", display: "Beta tester" },
];

const PLAN_DEFAULT_CONSULTAS = { free: 3, unlimited: 999999 };

const AdminPage = () => {
  const { user: me, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAll = useCallback(async (q = "") => {
    setLoading(true);
    setError("");
    try {
      const [d, u] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/users`, { params: q ? { q } : {} }),
      ]);
      setStats(d.data);
      setUsers(u.data.users || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Error cargando admin");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAll(search);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen w-full">
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Link to="/chat" data-testid="admin-back-chat" className="text-sm" style={{ color: "var(--pl-text-muted)" }}>
              <ArrowLeft className="w-4 h-4 inline" /> Volver al chat
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <img
              src="/logo-prelegal.png"
              alt="Pre-Legal.cl"
              width={32}
              height={32}
              style={{ width: 32, height: 32, objectFit: "contain", display: "block" }}
            />
            <h1 className="text-xl font-bold" style={{ color: "var(--pl-text-strong)", letterSpacing: "-0.01em" }}>
              Pre-Legal<span style={{ color: "var(--pl-primary)" }}>.cl</span> · Admin
            </h1>
          </div>
          <div className="flex items-center gap-3 text-sm" style={{ color: "var(--pl-text)" }}>
            <span>{me?.email}</span>
            <button data-testid="admin-logout" onClick={handleLogout} style={{ color: "var(--pl-text-muted)", padding: "4px 8px" }}>
              Salir
            </button>
          </div>
        </header>

        {error && (
          <div data-testid="admin-error" className="mb-6 p-3 rounded-lg text-sm" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }}>
            {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total usuarios" value={stats.total_users} testId="stat-total" />
            <StatCard label="Nuevos este mes" value={stats.new_users_month} testId="stat-month" />
            <StatCard label="Plan Mensual" value={stats.users_by_plan?.unlimited || 0} testId="stat-unlimited" />
            <StatCard label="Suscripciones Flow" value={stats.flow_active_subs} testId="stat-flow" />
          </div>
        )}

        {/* Create user */}
        <CreateUserCard onCreated={() => fetchAll(search)} />

        {/* Users list */}
        <Card title="Usuarios">
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por email"
              className="flex-1 rounded-lg px-3 py-2 outline-none text-sm"
              style={{ background: "#ffffff", border: "1px solid var(--pl-border-strong)", color: "var(--pl-text)" }}
            />
            <button data-testid="admin-search-btn" type="submit" style={smallBtn}>Buscar</button>
            {search && (
              <button type="button" data-testid="admin-search-clear" onClick={() => { setSearch(""); fetchAll(""); }} style={smallBtnGhost}>
                Limpiar
              </button>
            )}
          </form>

          {loading ? (
            <div className="py-10 text-center" style={{ color: "var(--pl-text-muted)" }}>
              <Loader2 className="w-5 h-5 animate-spin inline-block" />
            </div>
          ) : users.length === 0 ? (
            <p style={{ color: "var(--pl-text-muted)", fontSize: 13 }}>Sin resultados.</p>
          ) : (
            <div data-testid="admin-users-table" className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ color: "var(--pl-text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    <th style={th}>Email</th>
                    <th style={th}>Plan</th>
                    <th style={th}>Consultas</th>
                    <th style={th}>Etiqueta</th>
                    <th style={th}>Rol</th>
                    <th style={th}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow key={u.id} u={u} meEmail={me?.email} onChanged={() => fetchAll(search)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ---------- subcomponents ----------
const Card = ({ title, children }) => (
  <div
    className="rounded-2xl p-6 mb-6"
    style={{
      background: "var(--pl-card)",
      border: "1px solid rgba(212,162,74,0.25)",
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
    }}
  >
    <h2 className="text-lg font-semibold mb-4" style={{ color: "var(--pl-text-strong)", letterSpacing: "-0.01em" }}>
      {title}
    </h2>
    {children}
  </div>
);

const StatCard = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    className="rounded-xl p-5"
    style={{
      background: "var(--pl-card)",
      border: "1px solid rgba(212,162,74,0.25)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    }}
  >
    <div style={{ fontSize: 11, color: "var(--pl-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: "var(--pl-text-strong)", marginTop: 4, letterSpacing: "-0.02em" }}>{value}</div>
  </div>
);

const CreateUserCard = ({ onCreated }) => {
  const [form, setForm] = useState({
    email: "", password: "", name: "",
    plan: "free", consultas_remaining: 3, label: "beta_tester", role: "user",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const onChange = (k, v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      if (k === "plan" && PLAN_DEFAULT_CONSULTAS[v] !== undefined) {
        next.consultas_remaining = PLAN_DEFAULT_CONSULTAS[v];
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users`, {
        ...form,
        consultas_remaining: parseInt(form.consultas_remaining, 10) || 0,
      });
      setSuccess(`Cuenta creada: ${form.email}`);
      setForm({ email: "", password: "", name: "", plan: "free", consultas_remaining: 3, label: "beta_tester", role: "user" });
      onCreated && onCreated();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : (Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join(" ") : "Error creando usuario"));
    }
    setSubmitting(false);
  };

  return (
    <Card title="Crear cuenta (invitado / beta tester)">
      <form data-testid="admin-create-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Email" testId="create-email" type="email" value={form.email} onChange={(v) => onChange("email", v)} required />
        <Field label="Contraseña" testId="create-password" type="text" value={form.password} onChange={(v) => onChange("password", v)} placeholder="Mín. 6 caracteres" required />
        <Field label="Nombre (opcional)" testId="create-name" value={form.name} onChange={(v) => onChange("name", v)} />
        <Select label="Plan" testId="create-plan" value={form.plan} onChange={(v) => onChange("plan", v)} options={PLANS.map((p) => ({ value: p, display: p === "unlimited" ? "Plan Mensual" : p[0].toUpperCase() + p.slice(1) }))} />
        <Field label="Preguntas" testId="create-consultas" type="number" value={form.consultas_remaining} onChange={(v) => onChange("consultas_remaining", v)} />
        <Select label="Etiqueta" testId="create-label" value={form.label} onChange={(v) => onChange("label", v)} options={LABELS} />
        <Select label="Rol" testId="create-role" value={form.role} onChange={(v) => onChange("role", v)} options={[{ value: "user", display: "Usuario" }, { value: "admin", display: "Admin" }]} />
        <div className="md:col-span-2 lg:col-span-3 flex items-center gap-3 mt-2 flex-wrap">
          <button data-testid="admin-create-submit" type="submit" disabled={submitting} style={primaryBtn}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {submitting ? "Creando..." : "Crear cuenta"}
          </button>
          {success && <span data-testid="admin-create-success" style={{ color: "#059669", fontSize: 13 }}>✓ {success}</span>}
          {error && <span data-testid="admin-create-error" style={{ color: "#991b1b", fontSize: 13 }}>{error}</span>}
        </div>
      </form>
    </Card>
  );
};

const UserRow = ({ u, meEmail, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ plan: u.plan || "free", consultas_remaining: u.consultas_remaining ?? 0, label: u.label || "normal", role: u.role || "user" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/users/${encodeURIComponent(u.email)}`, {
        plan: draft.plan,
        consultas_remaining: parseInt(draft.consultas_remaining, 10) || 0,
        label: draft.label,
        role: draft.role,
      });
      setEditing(false);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || "Error actualizando");
    }
    setBusy(false);
  };

  const del = async () => {
    if (!window.confirm(`Eliminar ${u.email}? Esta acción es irreversible.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(u.email)}`);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || "Error eliminando");
    }
    setBusy(false);
  };

  const isMe = u.email === meEmail;

  return (
    <tr data-testid={`user-row-${u.email}`} style={{ borderTop: "1px solid rgba(212,162,74,0.15)" }}>
      <td style={td}>
        <div style={{ color: "var(--pl-text)" }}>{u.email}</div>
        {u.name && <div style={{ fontSize: 11, color: "var(--pl-text-subtle)" }}>{u.name}</div>}
      </td>
      <td style={td}>
        {editing ? (
          <select value={draft.plan} onChange={(e) => setDraft({ ...draft, plan: e.target.value })} style={inlineInput}>
            {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        ) : (
          <Badge color={u.plan === "unlimited" ? "gold" : (u.plan === "free" ? "gray" : "blue")}>
            {u.plan === "unlimited" ? "Mensual" : (u.plan || "free")}
          </Badge>
        )}
      </td>
      <td style={td}>
        {editing ? (
          <input type="number" value={draft.consultas_remaining} onChange={(e) => setDraft({ ...draft, consultas_remaining: e.target.value })} style={{ ...inlineInput, width: 80 }} />
        ) : (
          <span style={{ color: u.consultas_remaining === 0 ? "#fca5a5" : "#cbd5e1" }}>{u.plan === "unlimited" ? "∞" : (u.consultas_remaining ?? 0)}</span>
        )}
      </td>
      <td style={td}>
        {editing ? (
          <select value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} style={inlineInput}>
            {LABELS.map((l) => <option key={l.value} value={l.value}>{l.display}</option>)}
          </select>
        ) : (
          <Badge color={u.label === "beta_tester" ? "purple" : (u.label === "guest" ? "teal" : "gray")}>
            {LABELS.find((l) => l.value === u.label)?.display || "Normal"}
          </Badge>
        )}
      </td>
      <td style={td}>
        {editing ? (
          <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} style={inlineInput}>
            <option value="user">Usuario</option>
            <option value="admin">Admin</option>
          </select>
        ) : (
          <Badge color={u.role === "admin" ? "gold" : "gray"}>{u.role === "admin" ? "Admin" : "Usuario"}</Badge>
        )}
      </td>
      <td style={td}>
        <div className="flex gap-1">
          {editing ? (
            <>
              <button data-testid={`save-${u.email}`} onClick={save} disabled={busy} style={iconBtn} title="Guardar"><Check className="w-3.5 h-3.5" /></button>
              <button data-testid={`cancel-${u.email}`} onClick={() => { setEditing(false); setDraft({ plan: u.plan || "free", consultas_remaining: u.consultas_remaining ?? 0, label: u.label || "normal", role: u.role || "user" }); }} style={iconBtn} title="Cancelar"><X className="w-3.5 h-3.5" /></button>
            </>
          ) : (
            <>
              <button data-testid={`edit-${u.email}`} onClick={() => setEditing(true)} style={iconBtn} title="Editar"><Edit3 className="w-3.5 h-3.5" /></button>
              {!isMe && (
                <button data-testid={`del-${u.email}`} onClick={del} disabled={busy} style={iconBtnDanger} title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// ---------- styled primitives ----------
const Field = ({ label, testId, type = "text", value, onChange, placeholder, required }) => (
  <label className="block">
    <span style={{ fontSize: 11, color: "var(--pl-text)", letterSpacing: "0.02em", marginBottom: 4, display: "inline-block" }}>{label}</span>
    <input type={type} data-testid={testId} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg px-3 py-2 outline-none text-sm"
      style={{ background: "#ffffff", border: "1px solid var(--pl-border-strong)", color: "var(--pl-text)" }}
    />
  </label>
);

const Select = ({ label, testId, value, onChange, options }) => (
  <label className="block">
    <span style={{ fontSize: 11, color: "var(--pl-text)", letterSpacing: "0.02em", marginBottom: 4, display: "inline-block" }}>{label}</span>
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg px-3 py-2 outline-none text-sm"
      style={{ background: "#ffffff", border: "1px solid var(--pl-border-strong)", color: "var(--pl-text)" }}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.display}</option>)}
    </select>
  </label>
);

const Badge = ({ color, children }) => {
  const palette = {
    gold: { bg: "rgba(37, 99, 235, 0.10)", border: "rgba(37, 99, 235, 0.35)", color: "var(--pl-primary)" },
    blue: { bg: "rgba(59,130,246,0.18)", border: "rgba(59,130,246,0.4)", color: "#93c5fd" },
    purple: { bg: "rgba(147,51,234,0.18)", border: "rgba(147,51,234,0.4)", color: "#c4b5fd" },
    teal: { bg: "rgba(20,184,166,0.18)", border: "rgba(20,184,166,0.4)", color: "#5eead4" },
    gray: { bg: "rgba(100,116,139,0.18)", border: "rgba(100,116,139,0.35)", color: "var(--pl-text)" },
  }[color] || {};
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color }}>
      {children}
    </span>
  );
};

const th = { textAlign: "left", padding: "8px 8px", fontWeight: 600 };
const td = { padding: "10px 8px", verticalAlign: "middle" };
const inlineInput = { background: "rgba(15,23,42,0.7)", border: "1px solid var(--pl-border-strong)", color: "var(--pl-text)", padding: "4px 8px", borderRadius: 6, fontSize: 12 };
const primaryBtn = { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 8, background: "var(--pl-primary)", color: "#fff", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer" };
const smallBtn = { padding: "8px 14px", borderRadius: 8, background: "var(--pl-primary)", color: "#fff", fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer" };
const smallBtnGhost = { padding: "8px 14px", borderRadius: 8, background: "transparent", color: "var(--pl-text)", fontSize: 13, border: "1px solid var(--pl-border-strong)", cursor: "pointer" };
const iconBtn = { padding: 6, borderRadius: 6, background: "rgba(176,139,62,0.18)", color: "var(--pl-primary)", border: "1px solid var(--pl-border-strong)", cursor: "pointer", display: "inline-flex" };
const iconBtnDanger = { padding: 6, borderRadius: 6, background: "rgba(127,29,29,0.4)", color: "#991b1b", border: "1px solid rgba(248,113,113,0.3)", cursor: "pointer", display: "inline-flex" };

export default AdminPage;
